import { useEffect, useSyncExternalStore } from "react";

/**
 * Which Needs-you asks the person has already seen, so Home's nav item counts
 * only the new ones (founder 10-05: "show it until the user goes inside; once
 * seen, no red unless there is a new one").
 *
 * An ask is seen once the person has been on Home's Needs-you tab, or on the
 * ask's own task, while it was pending. Seen ids, not a time: opening one
 * task sees that task's asks and no other, and an id needs no clock to agree
 * with the server's.
 *
 * Per person, in this browser's storage: no per-user preference rail on the
 * server carries free-form marks like this one. Storage can be blocked
 * (Safari private mode, a policy), so this visit's marks also live in memory:
 * a blocked browser still clears the badge, it just forgets on reload.
 */
const KEY_PREFIX = "vestra:needs-you-seen:";
/** Newest marks kept: far more asks than ever wait at once (the read takes 50). */
const CAP = 200;
const NONE: ReadonlySet<string> = new Set();

const memory = new Map<string, ReadonlySet<string>>();
const listeners = new Set<() => void>();

function keyFor(userId: string): string {
  return `${KEY_PREFIX}${userId}`;
}

function load(userId: string): ReadonlySet<string> {
  const held = memory.get(userId);
  if (held) return held;
  let ids: string[] = [];
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(keyFor(userId)) ?? "[]"
    );
    if (Array.isArray(parsed))
      ids = parsed.filter((x): x is string => typeof x === "string");
  } catch {
    // Unreadable or blocked storage: nothing is seen yet on this device.
  }
  const seen = new Set(ids);
  memory.set(userId, seen);
  return seen;
}

/** Mark these asks seen for this person. A no-op when nothing is new. */
export function markNeedsYouSeen(userId: string, ids: readonly string[]): void {
  const prev = load(userId);
  const fresh = ids.filter((id) => !prev.has(id));
  if (fresh.length === 0) return;
  const next = new Set([...prev, ...fresh].slice(-CAP));
  memory.set(userId, next);
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify([...next]));
  } catch {
    // Storage is blocked: the marks last this visit.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab of the same browser marked asks seen: read its marks afresh.
  const onStorage = (e: StorageEvent) => {
    if (!e.key?.startsWith(KEY_PREFIX)) return;
    memory.delete(e.key.slice(KEY_PREFIX.length));
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** The ids this person has seen; empty on the server and with no person. */
export function useNeedsYouSeen(userId: string | null): ReadonlySet<string> {
  return useSyncExternalStore(
    subscribe,
    () => (userId ? load(userId) : NONE),
    () => NONE
  );
}

/**
 * The asks on screen right now are seen: Home's Needs-you tab while it is up,
 * the open task's own asks. One that arrives while it is on screen is seen too.
 */
export function useNeedsYouOnScreen(
  userId: string | null,
  ids: readonly string[]
): void {
  // Keyed by the ids themselves: callers derive a fresh array every render.
  const key = ids.join(",");
  useEffect(() => {
    if (userId && key) markNeedsYouSeen(userId, key.split(","));
  }, [userId, key]);
}

/** Tests only: forget this visit's marks. */
export function resetNeedsYouSeenForTest(): void {
  memory.clear();
}
